import { useGetCoreLocationCitiesInfiniteQuery } from '@autone/redux/core';
import { sentenceCase } from '@autone/utils';
import { forwardRef } from 'react';

import { VirtualMultiselect } from '../../virtual-multiselect';
import { CoreGeneralFilter } from '../components/CoreGeneralFilter';
import { SERVER_SIDE_SEARCH_LIMIT } from '../constants';
import type {
  CoreGeneralFilterProps,
  CoreGeneralFilterRef,
} from '../CoreGeneralFilters.types';
import { useCoreLocationGeneralFiltersContext } from '../hooks/useCoreLocationGeneralFiltersContext';

export const CoreLocationCitiesFilter = forwardRef<
  CoreGeneralFilterRef,
  CoreGeneralFilterProps
>(({ alwaysCommit, searchTerm, className }, ref) => {
  const {
    dimensionAttributeValue,
    setDimensionAttributeValue,
    coreLocationFiltersQueryArg,
  } = useCoreLocationGeneralFiltersContext('geographic.cities');

  const { items, isFetching, isFetchingNextPage, fetchNextPage } =
    useGetCoreLocationCitiesInfiniteQuery(
      {
        query: {
          limit: SERVER_SIDE_SEARCH_LIMIT,
          search_term: searchTerm,
        },
        body: {
          filters: coreLocationFiltersQueryArg,
        },
      },
      {
        selectFromResult: ({ data, ...rest }) => {
          return {
            ...rest,
            items: data?.pages.flatMap((page) => page.items) ?? [],
          };
        },
      },
    );

  return (
    <CoreGeneralFilter
      isFetching={isFetching && !isFetchingNextPage}
      onScrollEnd={fetchNextPage}
      ref={ref}
      selectedItems={dimensionAttributeValue}
      onSelectedItemsChange={setDimensionAttributeValue}
      alwaysCommit={alwaysCommit}
      className={className}
      items={items}
      renderItem={(item) => (
        <VirtualMultiselect.Item>
          <VirtualMultiselect.Checkbox />
          {sentenceCase(item.payload.description)}
        </VirtualMultiselect.Item>
      )}
    />
  );
});

CoreLocationCitiesFilter.displayName = 'CoreLocationCitiesFilter';
